import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { formatDateTime, formatRelative } from '../services/format';
import { useAlerts, useWorkerStatus } from './api';

/**
 * The Monitoring page's header (Admin, `monitoring.view`): the title, an optional action, the
 * description of the current view, and the Checks / Alerts tabs. The tabs are links to their own
 * addresses (/app/monitoring and /app/monitoring/alerts), so each view can be linked to directly.
 * The Alerts tab shows how many alerts are open, as the API reports them; above the tabs, whether
 * the monitoring worker itself is reporting.
 */
export function MonitoringHeader({
  current,
  action,
  children,
}: {
  current: 'checks' | 'alerts';
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1>Monitoring</h1>
        {action}
      </div>
      <div className="mt-1 max-w-3xl text-sm text-muted">{children}</div>
      <WorkerStatusStrip />
      <MonitoringTabs current={current} />
    </>
  );
}

const STRIP_TONES = {
  reporting: { box: 'border-line', dot: 'bg-success' },
  not_reporting: { box: 'border-danger-line bg-danger-soft/40', dot: 'bg-danger' },
  never_reported: { box: 'border-warning-line bg-warning-soft/40', dot: 'bg-warning' },
  error: { box: 'border-danger-line bg-danger-soft/40', dot: 'bg-danger' },
  loading: { box: 'border-line', dot: 'bg-line-strong' },
} as const;

/**
 * Whether the monitoring worker itself is running (Phase 07 Stage 03), from its heartbeat only.
 * Read when the page opens and on "Check again" — never on a timer. A time is shown only when the
 * worker has really reported.
 */
function WorkerStatusStrip() {
  const worker = useWorkerStatus();
  const tone = worker.isPending ? 'loading' : worker.isError ? 'error' : worker.data.state;
  const lastReport = (iso: string) => (
    <>
      Last report <time dateTime={iso}>{formatRelative(iso)}</time> ({formatDateTime(iso)})
    </>
  );

  let message: ReactNode;
  if (worker.isPending) message = 'Checking the monitoring worker…';
  else if (worker.isError) message = 'The worker status couldn’t be loaded.';
  else if (worker.data.state === 'reporting')
    message = (
      <>
        <span className="font-semibold text-fg">Monitoring worker is reporting</span>
        <Separator />
        <span className="block sm:inline">{lastReport(worker.data.lastSeenAt!)}</span>
      </>
    );
  else if (worker.data.state === 'not_reporting')
    message = (
      <>
        <span className="font-semibold text-fg">Monitoring worker is not reporting</span>
        <Separator />
        <span className="block sm:inline">
          {lastReport(worker.data.lastSeenAt!)}. Check results and alerts may be out of date until
          it reports again.
        </span>
      </>
    );
  else
    message = (
      <>
        <span className="font-semibold text-fg">Monitoring has not reported yet.</span> Current
        service health and alert status cannot be confirmed until the monitoring worker runs.
      </>
    );

  return (
    <section
      aria-label="Monitoring worker"
      className={`mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border px-4 py-3 text-sm ${STRIP_TONES[tone].box}`}
    >
      <p
        role={tone === 'error' ? 'alert' : worker.isPending ? 'status' : undefined}
        className="flex min-w-0 flex-1 items-start gap-2.5 text-fg-2"
      >
        <span
          aria-hidden="true"
          className={`mt-1.5 size-2 shrink-0 rounded-full ${STRIP_TONES[tone].dot}`}
        />
        <span className="min-w-0">{message}</span>
      </p>
      {worker.isPending ? null : (
        <button
          type="button"
          onClick={() => void worker.refetch()}
          disabled={worker.isFetching}
          className="shrink-0 rounded-lg border border-line-strong px-3 py-1.5 text-xs font-semibold text-fg hover:border-muted hover:bg-raised disabled:opacity-60"
        >
          {worker.isFetching ? 'Checking…' : 'Check again'}
        </button>
      )}
    </section>
  );
}

function MonitoringTabs({ current }: { current: 'checks' | 'alerts' }) {
  const open = useAlerts('open');
  const firstPage = open.data?.pages[0];
  const count = firstPage ? firstPage.items.length : 0;
  const more = Boolean(firstPage?.nextCursor);

  return (
    <nav
      aria-label="Monitoring views"
      className="mt-6 flex gap-1 overflow-x-auto border-b border-line"
    >
      <TabLink to="/app/monitoring" active={current === 'checks'}>
        Checks
      </TabLink>
      <TabLink
        to="/app/monitoring/alerts"
        active={current === 'alerts'}
        label={count > 0 ? `Alerts, ${count}${more ? ' or more' : ''} open` : undefined}
      >
        Alerts
        {count > 0 ? (
          <span
            aria-hidden="true"
            className="rounded-full bg-danger-soft px-2 py-px text-xs font-semibold text-danger"
          >
            {count}
            {more ? '+' : ''}
          </span>
        ) : null}
      </TabLink>
    </nav>
  );
}

function TabLink({
  to,
  active,
  label,
  children,
}: {
  to: string;
  active: boolean;
  /** The spoken name when the visible content (a count badge) is not enough on its own. */
  label?: string | undefined;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={`-mb-px inline-flex min-h-11 shrink-0 items-center gap-2 border-b-2 px-3 text-sm font-semibold ${
        active
          ? 'border-accent text-fg'
          : 'border-transparent text-muted hover:border-line-strong hover:text-fg'
      }`}
    >
      {children}
    </Link>
  );
}

/** " · " between the state and the last report; on phones the report starts its own line instead. */
function Separator() {
  return <span className="hidden sm:inline"> · </span>;
}
